import { DevMockBatchType, DevMockKind } from './constants';
import { DevMockSnapshot, fileNameForMock } from './snapshot';

function money(n: number): string {
    return (Math.round(n * 100) / 100).toFixed(2);
}

function formatDate(raw: string): string {
    const m = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        return `${m[2]}/${m[3]}/${m[1]}`;
    }
    return String(raw || '');
}

function canvasToFile(
    canvas: HTMLCanvasElement,
    fileName: string,
    quality: number
): Promise<File> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Unable to create mock image'));
                return;
            }
            resolve(new File([blob], fileName, { type: 'image/jpeg' }));
        }, 'image/jpeg', quality);
    });
}

function applyChallengingLook(ctx: CanvasRenderingContext2D, w: number, h: number, seed: number): void {
    ctx.save();
    ctx.globalAlpha = 0.08;
    for (let i = 0; i < 120; i++) {
        const x = Math.abs((seed * 13 + i * 97) % w);
        const y = Math.abs((seed * 29 + i * 53) % h);
        ctx.fillStyle = i % 2 ? '#000000' : '#ffffff';
        ctx.fillRect(x, y, 2 + (i % 3), 2 + (i % 2));
    }
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = '#c4b59a';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
    ctx.save();
    ctx.translate(w * 0.5, h * 0.5);
    ctx.rotate(((seed % 7) - 3) * Math.PI / 180);
    ctx.translate(-w * 0.5, -h * 0.5);
    ctx.restore();
}

function paintBase(
    title: string,
    p: DevMockSnapshot,
    batchType: DevMockBatchType
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
    const canvas = document.createElement('canvas');
    canvas.width = 850;
    canvas.height = 1100;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas unavailable');
    }
    ctx.fillStyle = batchType === 'challenging' ? '#f3efe6' : '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#0b3d66';
    ctx.fillRect(0, 0, canvas.width, 72);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px Arial';
    ctx.fillText(title, 28, 44);
    ctx.fillStyle = '#111111';
    ctx.font = '14px Arial';
    ctx.fillText(p.fullName, 28, 110);
    ctx.fillText('Alias: ' + p.alias, 28, 134);
    ctx.fillText(
        p.matched ? 'TEST MOCK — PROFILE MATCH' : 'TEST MOCK — INTENTIONAL MISMATCH',
        28,
        1060
    );
    if (batchType === 'challenging') {
        applyChallengingLook(ctx, canvas.width, canvas.height, p.seed);
    }
    return { canvas, ctx };
}

function paintPaystub(p: DevMockSnapshot, batchType: DevMockBatchType): HTMLCanvasElement {
    const { canvas, ctx } = paintBase('EMPLOYEE PAY STATEMENT', p, batchType);
    ctx.fillText('Employer: ' + p.employer, 28, 168);
    ctx.fillText('SSN: XXX-XX-' + p.ssnLast4, 28, 192);
    ctx.fillText('Stub start: ' + formatDate(p.incomePayStubStartDate), 28, 230);
    ctx.fillText('Stub end: ' + formatDate(p.incomePayStubEndDate), 28, 254);
    ctx.font = 'bold 18px Arial';
    ctx.fillText('STUB GROSS  $' + money(p.incomePayStubGross), 28, 310);
    ctx.font = '14px Arial';
    ctx.fillText('First: ' + p.firstName, 28, 350);
    ctx.fillText('Last: ' + p.lastName, 280, 350);
    return canvas;
}

function paintTax(p: DevMockSnapshot, batchType: DevMockBatchType): HTMLCanvasElement {
    const { canvas, ctx } = paintBase('Form W-2 Wage and Tax Statement', p, batchType);
    ctx.fillText('Tax year: ' + String(p.incomeIrsYear), 28, 168);
    ctx.fillText('Employee SSN: ' + p.ssn, 28, 192);
    ctx.fillText('Employer: ' + p.employer, 28, 230);
    ctx.font = 'bold 18px Arial';
    ctx.fillText('Box 1 Wages  $' + money(p.incomeIrsYearGross), 28, 290);
    ctx.font = '14px Arial';
    ctx.fillText('Employee: ' + p.fullName, 28, 340);
    return canvas;
}

function paintSsn(p: DevMockSnapshot, batchType: DevMockBatchType): HTMLCanvasElement {
    const { canvas, ctx } = paintBase('Social Security Card (Sample)', p, batchType);
    ctx.font = 'bold 28px Arial';
    ctx.fillText(p.ssn, 28, 220);
    ctx.font = '18px Arial';
    ctx.fillText(p.fullName.toUpperCase(), 28, 270);
    ctx.font = '14px Arial';
    ctx.fillText('SSN ' + p.ssn, 28, 320);
    ctx.fillText('Social Security Number', 28, 350);
    return canvas;
}

function paintReceipt(p: DevMockSnapshot, batchType: DevMockBatchType): HTMLCanvasElement {
    const { canvas, ctx } = paintBase('Travel Expense Receipt', p, batchType);
    ctx.fillText('Vendor: ' + p.vendor, 28, 168);
    ctx.fillText('Date: ' + formatDate(p.receiptDate), 28, 192);
    ctx.font = 'bold 18px Arial';
    ctx.fillText('Amount  $' + money(p.receiptAmount), 28, 250);
    ctx.font = '14px Arial';
    ctx.fillText('Guest: ' + p.fullName, 28, 300);
    return canvas;
}

export async function generateDevMockFile(
    kind: DevMockKind,
    snapshot: DevMockSnapshot,
    requestId: number,
    batchType: DevMockBatchType = 'clean'
): Promise<File> {
    let canvas: HTMLCanvasElement;
    switch (kind) {
        case 'paystub':
            canvas = paintPaystub(snapshot, batchType);
            break;
        case 'tax':
            canvas = paintTax(snapshot, batchType);
            break;
        case 'ssn':
            canvas = paintSsn(snapshot, batchType);
            break;
        case 'receipt':
            canvas = paintReceipt(snapshot, batchType);
            break;
        default:
            canvas = paintReceipt(snapshot, batchType);
            break;
    }
    const quality = batchType === 'challenging' ? 0.55 : batchType === 'mixed' ? 0.82 : 0.92;
    return canvasToFile(canvas, fileNameForMock(kind, requestId, batchType), quality);
}
